import React, { createContext, useContext, useState, useEffect } from 'react';
import { isAuthenticated, getUserData } from '../utils/authUtils';
import { login, logout, register } from '../services/authService';

// Create context
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileUpdated, setProfileUpdated] = useState(false); // Add this state
  const [company, setCompany] = useState([]);

  // Initialize auth state from local storage
  useEffect(() => {
    const initAuth = () => {
      try {
        if (isAuthenticated()) {
          setUser(getUserData());
        }
      } catch (err) {
        console.error('Error initializing auth state:', err);
        setError('Failed to restore authentication state');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  // Login handler
  const handleLogin = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await login(email, password);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Register handler
  const handleRegister = async (userData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await register(userData);
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message || 'Registration failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Logout handler
  const handleLogout = async () => {
    setLoading(true);

    try {
      await logout();
      setUser(null);
      setProfileUpdated(false);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompany = (companyData) => {
    setCompany(companyData);
  }


  // Context value
  const value = {
    user,
    setUser, // Expose setUser here
    loading,
    error,
    isAuthenticated: !!user,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    profileUpdated, 
    setProfileUpdated, 
    company,
    saveCompany: handleSaveCompany
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;

};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;